import { useCallback, useEffect, useRef, useState } from 'react';
import { checkLicensing, LicensingStatus } from '../services/licensingService';
import { supabase } from '../lib/supabaseClient';

interface UseLicensingState {
  status: LicensingStatus | null;
  isLoading: boolean;
  lastChecked: Date | null;
  error: string | null;
}

interface UseLicensingOptions {
  checkOnMount?: boolean;
  enablePeriodicChecks?: boolean;
  checkInterval?: number;
  salonId?: string | null;
  isSalonContextLoading?: boolean;
}

/** Fetches the authoritative licensing result without using browser storage. */
export function useLicensing(options: UseLicensingOptions = {}) {
  const {
    checkOnMount = true,
    enablePeriodicChecks = true,
    checkInterval = 15 * 60 * 1000,
    salonId = null,
    isSalonContextLoading = false,
  } = options;

  const [state, setState] = useState<UseLicensingState>({
    status: null,
    isLoading: true,
    lastChecked: null,
    error: null,
  });
  const requestIdRef = useRef(0);

  const performLicenseCheck = useCallback(async (showLoading = false): Promise<LicensingStatus | null> => {
    const requestId = ++requestIdRef.current;

    if (showLoading) {
      setState((previous) => ({ ...previous, isLoading: true, error: null }));
    }

    if (isSalonContextLoading) return null;

    try {
      const status = await checkLicensing(salonId);
      if (requestId !== requestIdRef.current) return status;

      setState({
        status,
        isLoading: false,
        lastChecked: new Date(),
        error: null,
      });
      return status;
    } catch (error) {
      console.error('License check failed:', error);
      if (requestId !== requestIdRef.current) return null;

      setState({
        status: null,
        isLoading: false,
        lastChecked: new Date(),
        error: 'license_check_failed',
      });
      return null;
    }
  }, [isSalonContextLoading, salonId]);

  const refreshLicense = useCallback(
    () => performLicenseCheck(true),
    [performLicenseCheck]
  );

  useEffect(() => {
    if (checkOnMount) void performLicenseCheck(true);
  }, [checkOnMount, performLicenseCheck]);

  useEffect(() => {
    if (!enablePeriodicChecks) return;

    const intervalId = window.setInterval(() => {
      void performLicenseCheck(false);
    }, checkInterval);

    return () => window.clearInterval(intervalId);
  }, [checkInterval, enablePeriodicChecks, performLicenseCheck]);

  useEffect(() => {
    const handleFocus = () => void performLicenseCheck(false);
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [performLicenseCheck]);

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        requestIdRef.current += 1;
        setState({ status: null, isLoading: true, lastChecked: null, error: null });
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return {
    status: state.status,
    isLoading: state.isLoading,
    lastChecked: state.lastChecked,
    error: state.error,
    refreshLicense,
  };
}
